import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BG = "https://images.unsplash.com/photo-1488972685288-c3fd157d7c7a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwYWJzdHJhY3QlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg3MzM5NjkxfDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div
        className="hidden lg:block relative bg-cover bg-center border-r border-[#111]"
        style={{ backgroundImage: `url(${BG})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b1020] via-[#0b1020]/30 to-transparent" />
        <div className="absolute bottom-0 left-0 p-12">
          <p className="font-mono text-xs tracking-widest text-[#111] bg-white px-2 py-1 inline-block mb-4">
            ACADEMIC CONTENT WRITING · CRM
          </p>
          <h1 className="font-heading text-5xl font-bold tracking-tighter text-white leading-none max-w-md">
            Track every lead. Every payment. In real time.
          </h1>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <p className="font-mono text-xs tracking-widest text-[#0047FF] mb-2">MHA WRITERS</p>
          <h2 className="font-heading text-4xl font-bold tracking-tighter mb-1">Sign in</h2>
          <p className="text-sm text-[#555] mb-8">Enter your work credentials to continue.</p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs font-mono uppercase tracking-wide">Email</Label>
              <Input
                id="email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@mhawriters.com"
                className="mt-1 rounded-none border-[#111] focus-visible:ring-2 focus-visible:ring-[#0047FF]"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-mono uppercase tracking-wide">Password</Label>
              <Input
                id="password" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1 rounded-none border-[#111] focus-visible:ring-2 focus-visible:ring-[#0047FF]"
                data-testid="login-password-input"
              />
            </div>
          </div>

          <Button
            type="submit" disabled={busy}
            className="w-full mt-8 rounded-none bg-[#0047FF] hover:bg-[#0035c4] text-white font-semibold tracking-wide h-11 transition-colors"
            data-testid="login-submit-button"
          >
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
